import axios from "axios";
import { isSessionDead, SessionUnrecoverableError } from "./sessionStatus";
import {
  ADMIN_SESSION_KEY,
  isJwtShaped,
  readStoredTokens
} from "./tokenStorage";
import { newAttemptId, trackEvent } from "./telemetry";

interface RefreshCallbacks {
  setTokens: (accessToken: string, refreshTokenId: string) => void;
  clearAuth: () => void;
  getRefreshTokenId: () => string | null | undefined;
  /**
   * Which session this caller belongs to. Required, so the compiler catches a caller that forgets:
   * an unkeyed caller used to join whatever flight happened to be in progress and receive its
   * outcome, which is how an embed's failed refresh signed the admin out (AB#10738).
   *
   * Use `ADMIN_SESSION_KEY` for anything rotating the browser-wide admin family. An embed holds an
   * exchanged token that is nobody else's business, so it passes a value unique to its own provider
   * instance.
   */
  sessionKey: string;
}

/**
 * Where a refresh was initiated from. Recorded because a proactive refresh that fails is a stalled
 * request while a reactive one is a request the user already watched fail, and the two are
 * different problems with different fixes (AB#10739).
 */
export type RefreshTrigger =
  | "proactive-interceptor"
  | "reactive-401"
  | "bootstrap"
  | "coreapi-proactive"
  | "coreapi-401"
  | "unknown";

interface TokenRefreshService {
  refreshToken(
    callbacks: RefreshCallbacks,
    presentedToken?: string,
    trigger?: RefreshTrigger
  ): Promise<string>;
}

const REFRESH_EVENT = "ClientTokenRefresh";

/**
 * Names what actually went wrong instead of letting every failure collapse into "refresh failed".
 *
 * The split that matters most is between outcomes that never reached the server at all --
 * `lock-timeout`, `timeout`, `network-error`, `no-refresh-id` -- and the ones that did. The first
 * group is invisible to any server-side instrumentation, which is why this module exists.
 */
function classifyFailure(error: unknown): string {
  if (error instanceof SessionUnrecoverableError) {
    // "cannot refresh" covers two very different things: an embed that never could (routine), and
    // a 2xx that was not a token (an outage). See SessionUnrecoverableError.code.
    return error.code;
  }

  const err = error as
    | { name?: string; code?: string; response?: { status?: number } }
    | undefined;

  // A Web Lock abort surfaces with no HTTP status, exactly like a network failure. Conflating them
  // would hide a 20s cross-tab stall behind what reads as a flaky connection.
  if (err?.name === "AbortError" || err?.name === "TimeoutError") {
    return "lock-timeout";
  }

  if (err?.code === "ECONNABORTED" || err?.code === "ETIMEDOUT") {
    return "timeout";
  }

  const status = err?.response?.status;
  if (typeof status === "number") {
    if (status === 401) return "http-401";
    if (status === 400) return "http-400";
    if (status >= 500) return "http-5xx";
    return `http-${status}`;
  }

  return "network-error";
}

const REFRESH_LOCK_NAME = "publicinput.internal-token-refresh";

/**
 * Isolating a caller that supplied no key keeps it from receiving someone else's outcome, which is
 * the fail-safe direction: forgetting a key should cost an extra round trip, never a stray sign-out.
 */
let isolatedFlightCounter = 0;

function nextIsolatedKey(): string {
  isolatedFlightCounter += 1;
  return `__isolated-${isolatedFlightCounter}`;
}

function tunable(key: string, fallback: number): number {
  const configured = (
    window._JSCONFIG as unknown as Record<string, unknown> | undefined
  )?.[key];
  return typeof configured === "number" && configured > 0
    ? configured
    : fallback;
}

const refreshTimeoutMs = () => tunable("RefreshRequestTimeoutMs", 15000);
const lockWaitMs = () => tunable("RefreshLockWaitMs", 20000);

const ADOPTION_MIN_LIFE_MS = 30000;

/**
 * The page's MVC synchronizer token, or "1" when the page has no form to take it from. The
 * server treats this header as an origin signal and does not verify the value, so a refresh
 * that fires before any form has rendered still gets through; sending the real token when one
 * exists keeps the request consistent with every other AJAX caller in the app.
 */
function antiForgeryToken(): string {
  if (typeof document === "undefined") {
    return "1";
  }

  const field = document.querySelector<HTMLInputElement>(
    'input[name="__RequestVerificationToken"]'
  );

  return field?.value || "1";
}

/**
 * Serializes rotation across tabs.
 *
 * The name is keyed per session so two genuinely independent sessions do not queue behind each
 * other; every admin tab passes the same `ADMIN_SESSION_KEY`, so the cross-tab serialization this
 * lock exists for is unchanged.
 *
 * **The admin key keeps the bare name**, and that is the whole point rather than an oversight.
 * Suffixing it would have created two different locks and broken the guarantee this comment claims:
 * an unkeyed caller — which might be rotating the admin family — falls back to the bare name, so if
 * admin held `...:admin` the fallback would serialize against nothing. It would also stop
 * serializing against any tab still running the previous bundle for the length of a deploy, and two
 * tabs rotating one family means the second presents a secret the first rotated out: replay
 * detection, family revoked, signed out everywhere.
 *
 * So only a genuinely separate session — an embed — gets its own lock. Isolate the subscribers;
 * share the lock unless the session is provably unrelated.
 */
function runExclusive<T>(sessionKey: string | null, work: () => Promise<T>): Promise<T> {
  const locks =
    typeof navigator !== "undefined"
      ? (navigator as Navigator & { locks?: LockManager }).locks
      : undefined;

  if (!locks || typeof locks.request !== "function") {
    return work();
  }

  const signal =
    typeof AbortSignal !== "undefined" && "timeout" in AbortSignal
      ? AbortSignal.timeout(lockWaitMs())
      : undefined;

  const lockName =
    sessionKey && sessionKey !== ADMIN_SESSION_KEY
      ? `${REFRESH_LOCK_NAME}:${sessionKey}`
      : REFRESH_LOCK_NAME;

  return locks.request(
    lockName,
    signal ? { signal } : {},
    work
  ) as Promise<T>;
}

function decodePayload(
  token: string | null | undefined
): { exp?: number; sub?: string; nameid?: string } | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;

  try {
    return JSON.parse(
      decodeURIComponent(
        atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
  } catch (_) {
    return null;
  }
}

function principalOf(
  payload: { sub?: string; nameid?: string } | null
): string | null {
  return payload?.sub || payload?.nameid || null;
}

// Read as a pair: adopting an access token minted by another tab alongside a refresh id
// from a different rotation guarantees the next refresh fails, so both must be present and
// must be read together.
function readAdoptableToken(
  presentedToken: string
): { accessToken: string; tokenId: string } | null {
  const { accessToken: token, refreshTokenId: tokenId } = readStoredTokens();

  if (!token || !tokenId || token === presentedToken) return null;

  const payload = decodePayload(token);
  if (typeof payload?.exp !== "number") return null;
  if (payload.exp * 1000 <= Date.now() + ADOPTION_MIN_LIFE_MS) return null;

  const presentedPrincipal = principalOf(decodePayload(presentedToken));
  if (!presentedPrincipal || principalOf(payload) !== presentedPrincipal) {
    return null;
  }

  return { accessToken: token, tokenId };
}

class TokenRefreshServiceImpl implements TokenRefreshService {
  /**
   * Flights in progress, keyed by session. Was a single boolean, which is the whole defect: one
   * in-flight refresh owned every waiter regardless of which session it belonged to (AB#10738).
   */
  private inFlight = new Set<string>();

  /**
   * The in-flight leader's correlation id, so waiters join to the same server event. Keyed by
   * session for the same reason the flights are: with per-session flights a single field would
   * hand a waiter the attempt id of whichever session started last (AB#10738, AB#10739).
   */
  private currentAttemptId = new Map<string, string>();

  private refreshSubscribers = new Map<
    string,
    Array<{
      resolve: (token: string) => void;
      reject: (err: unknown) => void;
      callbacks: RefreshCallbacks;
    }>
  >();

  private subscribeTokenRefresh(
    sessionKey: string,
    resolve: (token: string) => void,
    reject: (err: unknown) => void,
    callbacks: RefreshCallbacks
  ) {
    const waiting = this.refreshSubscribers.get(sessionKey) ?? [];
    waiting.push({ resolve, reject, callbacks });
    this.refreshSubscribers.set(sessionKey, waiting);
  }

  /** Takes the waiters for one session and clears them, so a fan-out can never reach another. */
  private drain(sessionKey: string) {
    const waiting = this.refreshSubscribers.get(sessionKey) ?? [];
    this.refreshSubscribers.delete(sessionKey);
    return waiting;
  }

  private onTokenRefreshed(
    sessionKey: string,
    accessToken: string,
    refreshTokenId: string
  ) {
    // Each waiter is settled in its own try/finally. These callbacks are other bundles' code -
    // setTokens reaches React state and clearAuth reaches storage - so one of them throwing is not
    // hypothetical, and an unguarded forEach would abort mid-list. The bucket is already drained at
    // that point, so every later waiter would be neither resolved nor rejected: a permanently
    // pending promise inside an axios interceptor, i.e. a request that hangs with no error.
    this.drain(sessionKey).forEach(({ resolve, callbacks }) => {
      try {
        callbacks.setTokens(accessToken, refreshTokenId);
      } catch (callbackError) {
        console.error("A refresh subscriber's setTokens threw:", callbackError);
      } finally {
        resolve(accessToken);
      }
    });
  }

  private onRefreshFailed(
    sessionKey: string,
    error: unknown,
    sessionDead: boolean
  ) {
    // Same guarantee as onTokenRefreshed: one waiter's clearAuth throwing must not leave the
    // waiters behind it hanging forever.
    this.drain(sessionKey).forEach(({ reject, callbacks }) => {
      try {
        if (sessionDead) callbacks.clearAuth();
      } catch (callbackError) {
        console.error("A refresh subscriber's clearAuth threw:", callbackError);
      } finally {
        reject(error);
      }
    });
  }

  private async rotate(
    callbacks: RefreshCallbacks,
    presentedToken: string | undefined,
    attemptId: string,
    trigger: RefreshTrigger
  ): Promise<{ accessToken: string; tokenId: string; adopted: boolean }> {
    const adopted = presentedToken ? readAdoptableToken(presentedToken) : null;

    if (adopted) {
      // Reported as its own outcome rather than folded into success. This path returns without any
      // server round trip, so counting it as a refresh would mask a server that stopped answering.
      return { ...adopted, adopted: true };
    }

    // A *missing* id is no longer terminal. The server resolves the id from its own cookie and,
    // failing that, reissues against the live family from the signed-in session, so the call is
    // worth making even with nothing stored here -- that is exactly the state of a tab whose
    // first request fires before the tokens have been seeded.
    //
    // An *empty* id is different, and the distinction is load-bearing. AuthProvider hands embed
    // and public-dashboard pages `refreshTokenId: ""` to mean "this session can never refresh":
    // they hold an exchanged access token and no MVC identity cookie, so the server's fallback
    // can only ever refuse them. Asking anyway would spend a round trip -- up to the refresh
    // timeout, on kiosk networks -- to reach the answer we already have, and the public
    // dashboard is the single largest source of 401s in production. Nullish means "nothing
    // stored yet, worth asking"; "" means "asked and answered".
    const refreshTokenId = callbacks.getRefreshTokenId();

    if (refreshTokenId === "") {
      throw new SessionUnrecoverableError("Session cannot refresh");
    }

    const authClient = axios.create({
      baseURL: window._JSCONFIG.ReactAppAuthBaseUrl,
      withCredentials: true,
      timeout: refreshTimeoutMs(),
      // Proof of same-site origin, and the gate on the signed-in-session fallback. The identity
      // cookie is SameSite=None, so without a header a cross-site auto-submitting form would
      // reach that fallback fully authenticated; a form cannot set headers, and a cross-origin
      // XHR that sets one is forced into a preflight this app does not answer. The page's
      // synchronizer token is sent when one is on the page, but presence is what the server
      // checks -- a bootstrap refresh can fire before any form has rendered.
      headers: {
        "X-Request-Verification-Token": antiForgeryToken(),
        // Joins this attempt to the server's TokenRefreshOutcome event for the same request, which
        // is the only way to tell a client-side failure from a server-side denial after the fact.
        "X-Refresh-Attempt-Id": attemptId,
        // Derived, not hard-coded. Both clients call this one rotate(), so a constant here would
        // report every CoreApiClient refresh as the axios provider and make the dimension a lie.
        "X-Refresh-Client": trigger.indexOf("coreapi") === 0 ? "coreapi" : "api-provider"
      }
    });

    // The server prefers its own cookie for the token id; TokenId is sent only as a
    // fallback for clients whose id cookie is missing.
    const { data } = await authClient.post<{
      accessToken: string;
      tokenId: string;
    }>("/account/refresh", refreshTokenId ? { TokenId: refreshTokenId } : {});

    // The response body is a trust boundary, and it was not being treated as one. A 2xx that is
    // not the token JSON -- an HTML error or login page arrived at through a redirect, an empty
    // body, a proxy interstitial -- yields `undefined` here. That value used to be returned,
    // handed to setTokens, and persisted as the literal string "undefined", after which every
    // request went out unauthenticated and no reload could recover the session (AB#10507).
    //
    // SessionUnrecoverableError, rather than a bare throw: this session genuinely cannot be
    // refreshed, so the caller should clear the stored credentials and send the user to log in
    // instead of retrying against a server that just failed to issue a token.
    if (!isJwtShaped(data?.accessToken)) {
      throw new SessionUnrecoverableError(
        "Refresh response did not contain an access token",
        "malformed-response"
      );
    }

    // The id is not the poison vector and the server always sends it, but a missing one must not
    // become `undefined`: fall back to the id we presented so the family stays addressable.
    const issuedTokenId =
      typeof data?.tokenId === "string" && data.tokenId.length > 0
        ? data.tokenId
        : (refreshTokenId ?? "");

    return { accessToken: data.accessToken, tokenId: issuedTokenId, adopted: false };
  }

  async refreshToken(
    callbacks: RefreshCallbacks,
    presentedToken?: string,
    trigger: RefreshTrigger = "unknown"
  ): Promise<string> {
    // A caller with no key is isolated rather than folded into whatever flight is running. The
    // compiler requires sessionKey, so this only catches JS callers and test fixtures - but it is
    // the direction that matters: forgetting a key must cost an extra round trip, never someone
    // else's sign-out.
    const declaredKey = callbacks.sessionKey;
    const sessionKey = declaredKey || nextIsolatedKey();

    if (!declaredKey) {
      console.error(
        "TokenRefreshService: refresh requested with no sessionKey; isolating this flight (AB#10738)."
      );
    }

    // Read from the session key, not from storage. The key is the real form of the distinction the
    // old `getRefreshTokenId() === ""` test was standing in for -- an embed holds an exchanged
    // token keyed to its own provider instance, every admin tab shares ADMIN_SESSION_KEY -- and it
    // is readable by a waiter, which never reaches rotate at all (AB#10738, AB#10739).
    const sessionKind = !declaredKey
      ? "unknown"
      : declaredKey === ADMIN_SESSION_KEY
        ? "admin"
        : "embed";

    if (this.inFlight.has(sessionKey)) {
      const waiterStartedAt = Date.now();
      // The leader's id for THIS session, not a fresh one: a waiter issued no request of its own,
      // so its own id would join to nothing and every waiter row would drop out of a
      // client-to-server join.
      const waitingOn = this.currentAttemptId.get(sessionKey) ?? "";

      return new Promise<string>((resolve, reject) => {
        this.subscribeTokenRefresh(sessionKey, resolve, reject, callbacks);
      }).then(
        (token) => {
          trackEvent(
            REFRESH_EVENT,
            {
              outcome: "rotated",
              trigger,
              role: "waiter",
              sessionKind,
              attemptId: waitingOn
            },
            { durationMs: Date.now() - waiterStartedAt, lockWaitMs: 0 }
          );
          return token;
        },
        (error) => {
          // Always sent, never sampled. A waiter that fails is a request the user watched fail, and
          // sampling those leaves the count looking healthy exactly when it is not.
          trackEvent(
            REFRESH_EVENT,
            {
              outcome: classifyFailure(error),
              trigger,
              role: "waiter",
              sessionKind,
              attemptId: waitingOn
            },
            { durationMs: Date.now() - waiterStartedAt, lockWaitMs: 0 },
            true
          );
          throw error;
        }
      );
    }

    this.inFlight.add(sessionKey);

    const startedAt = Date.now();
    // Timing the lock separately from the request is the whole point of timing either one: a 20s
    // cross-tab stall and a 15s server timeout look identical from outside and have nothing in
    // common as problems.
    let lockAcquiredAt = startedAt;
    // Tracked explicitly rather than inferred from the timestamp. When the Web Lock request aborts
    // on its timeout the callback never runs, so lockAcquiredAt stays at startedAt and the elapsed
    // lock wait computes to 0 -- on the one outcome the measurement exists to capture. It also
    // keeps a genuine 0 (no Web Locks support, so no wait) distinguishable from a 20s stall.
    let lockAcquired = false;
    const attemptId = newAttemptId();
    this.currentAttemptId.set(sessionKey, attemptId);

    try {
      // Only a declared key gets a keyed lock. An isolated flight shares the global one, because it
      // might be rotating the admin family and a private lock would let it race a keyed flight
      // against the same credential.
      const { accessToken, tokenId, adopted } = await runExclusive(
        declaredKey || null,
        () => {
          lockAcquiredAt = Date.now();
          lockAcquired = true;
          return this.rotate(callbacks, presentedToken, attemptId, trigger);
        }
      );

      // Released BEFORE the fan-out, not in a finally after it. A subscriber callback can
      // synchronously call refreshToken again for the same key; if the key were still marked
      // in-flight it would subscribe to a bucket that has just been drained and will never be
      // drained again.
      this.inFlight.delete(sessionKey);
      this.currentAttemptId.delete(sessionKey);

      callbacks.setTokens(accessToken, tokenId);
      this.onTokenRefreshed(sessionKey, accessToken, tokenId);

      trackEvent(
        REFRESH_EVENT,
        {
          outcome: adopted ? "adopted-from-tab" : "rotated",
          trigger,
          role: "leader",
          sessionKind,
          attemptId
        },
        {
          durationMs: Date.now() - startedAt,
          lockWaitMs: lockAcquiredAt - startedAt
        }
      );

      return accessToken;
    } catch (error) {
      this.inFlight.delete(sessionKey);
      this.currentAttemptId.delete(sessionKey);

      const sessionDead = isSessionDead(error);
      if (sessionDead) callbacks.clearAuth();

      trackEvent(
        REFRESH_EVENT,
        {
          outcome: classifyFailure(error),
          trigger,
          role: "leader",
          sessionKind,
          attemptId,
          sessionDead: sessionDead ? "true" : "false",
          lockAcquired: lockAcquired ? "true" : "false"
        },
        {
          durationMs: Date.now() - startedAt,
          // Never acquired: the whole elapsed time WAS the wait.
          lockWaitMs: lockAcquired
            ? lockAcquiredAt - startedAt
            : Date.now() - startedAt
        },
        true
      );

      this.onRefreshFailed(sessionKey, error, sessionDead);
      throw error;
    }
  }
}

declare global {
  interface Window {
    __TokenRefreshService?: TokenRefreshService;
  }
}

if (!window.__TokenRefreshService) {
  window.__TokenRefreshService = new TokenRefreshServiceImpl();
}

export const TokenRefreshService = window.__TokenRefreshService;
